import API from "./api";
import AddressValidation from "./address-validation";
import Settings from "./settings";

/**
 * Autocomplete class that shows address autocompletion. Its uses the API class to search and retrieve address information.
 */
class Autocomplete {
	/**
	 * Autocomplete class constructor
	 *
	 * @param {HTMLElement} container
	 * @param {HTMLElement} addressLine1Input
	 * @param {Object} selectors
	 * @param {string|null} validationKey Identifies this address group to the server side validator.
	 */
	constructor(container, addressLine1Input, selectors, validationKey = null) {
		this.container = container;
		this.addressLine1Input = addressLine1Input;
		this.selectors = selectors;
		this.validationKey = validationKey;
	}

	/**
	 * Should this address group report autocomplete selections to the server side validator?
	 *
	 * @returns {boolean}
	 */
	tracksValidatedState() {
		return Boolean(this.validationKey) && Settings.isAddressValidationEnabled();
	}

	/**
	 * Get the form this address group is submitted with.
	 *
	 * @returns {HTMLElement|null}
	 */
	getForm() {
		return this.addressLine1Input ? this.addressLine1Input.closest("form") : null;
	}

	/**
	 * Get the mapped address inputs, in the order the validated state string is built from.
	 *
	 * @returns {Array}
	 */
	getMappedElements() {
		const $container = jQuery(this.container);

		return [
			this.selectors.AddressLine1,
			this.selectors.AddressLine2,
			this.selectors.City,
			this.selectors.State,
			this.selectors.Postcode,
		].map((selectors) => {
			if (!Array.isArray(selectors)) {
				selectors = [selectors];
			}

			for (const selector of selectors) {
				const $element = $container.find(selector);
				if ($element.length) {
					return $element[0];
				}
			}

			return null;
		});
	}

	/**
	 * Re-attach the validated state watchers after the address inputs have been replaced.
	 *
	 * WooCommerce's country-select.js swaps the state field for a brand new node whenever the
	 * country changes, leaving the listeners attached at init() on a detached element. The
	 * selection is cleared at the same time: a country change means the address is no longer the
	 * one that came out of the autocomplete.
	 */
	refreshValidationWatch() {
		if (this.container === null || this.addressLine1Input === null || !this.tracksValidatedState()) {
			return;
		}

		const form = this.getForm();

		AddressValidation.clearSelection(form, this.validationKey);
		AddressValidation.watch(form, this.validationKey, () => this.getMappedElements());
	}

	/**
	 * Show loading spinner on the address input field
	 */
	showLoading() {
		jQuery(this.addressLine1Input).closest('div').addClass('datatools-address-loading');
	}

	/**
	 * Hide loading spinner from the address input field
	 */
	hideLoading() {
		jQuery(this.addressLine1Input).closest('div').removeClass('datatools-address-loading');
	}

	/**
	 * Initialise the autocomplete field
	 */
	init() {
		const _this = this;

		//Ensure that the container and address line 1 input elements exist in the DOM
		if (this.container === null || this.addressLine1Input === null) {
			return;
		}

		if (this.addressLine1Input.dataset.datatoolsAutocompleteInit) {
			return;
		}
		this.addressLine1Input.dataset.datatoolsAutocompleteInit = '1';

		API.init();

		if (this.tracksValidatedState()) {
			AddressValidation.watch(this.getForm(), this.validationKey, () => this.getMappedElements());
		}

		const $addressLine1Input = jQuery(this.addressLine1Input);
		const $container = jQuery(this.container);

		const close = function () {
			$addressLine1Input.autocomplete("close");
		};

		//Attach the jQuery autocomplete field to the address line 1 input
		$addressLine1Input.autocomplete({
			html: true,
			minLength: 8,
			appendTo: $addressLine1Input.closest('[data-elementor-type="popup"]')[0] || 'body',
			source: function (request, response) {
				// Show loading state
				_this.showLoading();

				API.searchAddress(request.term).then(function (data) {
					// Remove loading state
					_this.hideLoading();

					let results = [];

					if (data.DtResponse.Result.length > 0) {
						results = data.DtResponse.Result.map(function (item) {
							let Output = (item.AddressLine + ", " + item.Locality + ", " + item.State + ", " + item.Postcode);
							return {
								label: Output,
								value: Output,
								address: item,
							};
						});
					}

					results.push({
						label: "<b>Don't see your address?</b> Search again or <span class=\"manual-entry\">enter an address manually</span>.",
						value: "dont_see_address"
					});

					response(results);
				}, () => {
					// Handle API error
					_this.hideLoading();
					$addressLine1Input.autocomplete("close");
				});
			},

			select: function (event, ui) {
				event.preventDefault();

				const updateValue = function ($element, value) {
					if ($element.length > 0) {
						$element.val(value);
						$element[0].dispatchEvent(new Event('change'));
					}
				};

				const updateAddressField = (selectors, value) => {
					if (!Array.isArray(selectors)) {
						selectors = [selectors];
					}
					selectors.forEach(selector => {
						const $element = $container.find(selector);
						if ($element.length) {
							updateValue($element, value);
						}
					});
				};

				if (ui.item.value === 'dont_see_address') {
					$addressLine1Input.autocomplete("close");
					updateValue($addressLine1Input, "");
					return;
				}

				// Show loading state during address retrieval
				_this.showLoading();

				API.retrieveAddress(ui.item.address.RecordId).then((data) => {
					// Remove loading state
					_this.hideLoading();

					const address = API.formatAddress(data);

					const mappings = [
						{
							selector: _this.selectors.AddressLine1,
							value: address.AddressLine1,
						},
						{
							selector: _this.selectors.AddressLine2,
							value: address.AddressLine2,
						},
						{
							selector: _this.selectors.City,
							value: address.City,
						},
						{
							selector: _this.selectors.State,
							value: address.State,
						},
						{
							selector: _this.selectors.Postcode,
							value: address.Postcode,
						},
					];

					mappings.forEach(mapping => updateAddressField(mapping.selector, mapping.value));

					// Recorded from the inputs rather than the API response: if a mapped select has
					// no matching option the submitted value differs from what Kleber returned, and
					// the server would then pay for a verification call it did not need to.
					if (_this.tracksValidatedState()) {
						AddressValidation.recordSelection(_this.getForm(), _this.validationKey, _this.getMappedElements());
					}
				}, () => {
					// Handle API error
					_this.hideLoading();
					$addressLine1Input.autocomplete("close");
				});
			}
		});
	}
}

export default Autocomplete;
